import type { HashMap } from "@certa/types";
import type { Change, LedgerTxMetaData, Transaction } from "../types";
import { getNanoId } from "./";
import { isEqual } from "lodash-es";

/**
 * Combines transactions into a single transaction with all different
 * changes combined into one. This is useful when multiple transactions
 * contain changes to the same component.
 * @param transactions
 */
export const mergeTransactions = (transactions: Transaction[]) => {
  const combinedTransaction: Transaction = {
    id: transactions[0].id,
    changes: []
  };

  // To store paths that were seen while traversing the transactions
  const knownPaths = new Set<string>();

  // To store paths, where from is undefined, which means that this is
  // a new component
  const newlyAddedPaths = new Set<string>();

  transactions.forEach(transaction => {
    transaction.changes.forEach(change => {
      if (!knownPaths.has(change.path)) {
        knownPaths.add(change.path);
        combinedTransaction.changes.push(change);

        if (change.from === undefined) {
          newlyAddedPaths.add(change.path);
        }
      } else {
        if (change.to === undefined && newlyAddedPaths.has(change.path)) {
          // This is a situation where something was added and then deleted.
          // Therefore the delta is 0 and we can remove it from the transaction altogether.
          combinedTransaction.changes = combinedTransaction.changes.filter(
            c => c.path !== change.path
          );

          // Remove it from the newly added paths as well
          newlyAddedPaths.delete(change.path);
          return;
        }

        // Otherwise, this could be an update.
        const findIndex = combinedTransaction.changes.findIndex(
          c => c.path === change.path
        );
        if (findIndex === -1) {
          // Should be unreachable
          throw new Error(`Change with path ${change.path} not found`);
        }
        combinedTransaction.changes.splice(findIndex, 1, change);
      }
    });
  });

  return combinedTransaction;
};

/**
 * Creates a new transaction for the given changes
 */
export const createTransaction = (
  changes: Change[],
  transactionMetadata: LedgerTxMetaData = {}
) => {
  return {
    id: getNanoId(),
    changes,
    transactionMetadata
  };
};

export function getChangedProperties(
  obj1: HashMap = {},
  obj2: HashMap = {}
): string[] {
  const propertiesChanged: Set<string> = new Set();

  // Get all unique property names from both objects
  const allProps = new Set([...Object.keys(obj1), ...Object.keys(obj2)]);

  // Check for properties that are different
  allProps.forEach(prop => {
    if (
      !(prop in obj1) ||
      !(prop in obj2) ||
      !isEqual(obj1[prop], obj2[prop])
    ) {
      propertiesChanged.add(prop);
    }
  });

  return Array.from(propertiesChanged);
}
